import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useCancelarOS } from '@/hooks/useOrdensServico';
import { OrdemServico } from '@/types/ordemServico';
import { Loader2, Ban } from 'lucide-react';

interface CancelarOSDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  os: OrdemServico;
}

export function CancelarOSDialog({ open, onOpenChange, os }: CancelarOSDialogProps) {
  const { t } = useTranslation();
  const [motivo, setMotivo] = useState('');
  const cancelarOS = useCancelarOS();

  const handleCancelar = async () => {
    if (!motivo.trim()) return;
    
    await cancelarOS.mutateAsync({
      id: os.id,
      motivo: motivo.trim(),
    });
    setMotivo('');
    onOpenChange(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setMotivo('');
    }
    onOpenChange(open);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-destructive" />
            {t('orders.cancel.title')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            <span dangerouslySetInnerHTML={{ __html: t('orders.cancel.description', { number: os.numero_os }) }} />
            <br />
            <br />
            {t('orders.cancel.warning')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="motivo">
            {t('orders.cancel.reasonLabel')} <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="motivo"
            placeholder={t('orders.cancel.reasonPlaceholder')}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
          />
          {!motivo.trim() && (
            <p className="text-xs text-muted-foreground">
              {t('orders.cancel.reasonRequired')}
            </p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={cancelarOS.isPending}>
            {t('common.back')}
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleCancelar}
            disabled={cancelarOS.isPending || !motivo.trim()}
          >
            {cancelarOS.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('orders.cancel.cancelling')}
              </>
            ) : (
              <>
                <Ban className="h-4 w-4 mr-2" />
                {t('orders.cancel.confirm')}
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
